import { useEffect, useState, useCallback } from 'react';

/**
 * Hook pro Live tab — udržuje aktuální seznam vozidel + napojuje se na Reverb
 * privátní kanál `flotila.live`. Při příchodu eventu `pozice.updated` aktualizuje
 * lat/lng/rychlost/směr vozidla v lokálním state.
 *
 * Mapa si markery spravuje sama přes setLatLng — tento hook neřídí re-render mapy.
 */
export default function useFlotilaLive() {
    const [vozidla, setVozidla] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const refetch = useCallback(() => {
        setLoading(true);
        return fetch('/api/flotila/vozidla')
            .then((r) => r.json())
            .then((data) => {
                if (Array.isArray(data)) setVozidla(data);
                else if (data?.error) setError(data.error);
                setLoading(false);
            })
            .catch((e) => {
                setError(e.message);
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        // Sekvenční init — nejprve vozidla, pak teprve polohy. Bez toho byl
        // race: polohy.then resolved před vozidla.then a setVozidla(map) nad
        // prázdným polem zahodil pozice; pak vozidla resolved a vykreslila se
        // bez lat/lng. Marker auta na mapě nikdy nedorazil.
        refetch().then(() => {
            fetch('/api/flotila/gps/polohy')
                .then((r) => r.json())
                .then((polohy) => {
                    if (!Array.isArray(polohy)) return;
                    setVozidla((prev) => prev.map((v) => {
                        const p = polohy.find((x) => x.vozidlo_id === v.id);
                        return p ? {
                            ...v,
                            lat: p.lat, lng: p.lng,
                            rychlost_kmh: p.rychlost, smer: p.smer,
                            zaznameno: p.zaznameno,
                            ignition: p.ignition,
                            motion: p.motion,
                            power_v: p.power_v,
                        } : v;
                    }));
                })
                .catch(() => {});
        });

        // Polling /api/flotila/vozidla každých 30 s — aktualizuje neživé fieldy
        // (doba_dnes, km_dnes, počet jízd, kalibrace). Position fields
        // (lat/lng/rychlost/ignition/motion/power) zůstávají z Reverbu, protože
        // ty jsou čerstvější. Pokud Reverb hodnotu ještě neposlal, vezmeme ji
        // z refetchu.
        const refetchInterval = setInterval(() => {
            fetch('/api/flotila/vozidla')
                .then((r) => r.json())
                .then((data) => {
                    if (!Array.isArray(data)) return;
                    setVozidla((prev) => prev.map((v) => {
                        const fresh = data.find((x) => x.id === v.id);
                        if (!fresh) return v;
                        return {
                            ...fresh,
                            lat: v.lat ?? fresh.lat,
                            lng: v.lng ?? fresh.lng,
                            rychlost_kmh: v.rychlost_kmh ?? fresh.rychlost_kmh,
                            smer: v.smer ?? fresh.smer,
                            zaznameno: v.zaznameno ?? fresh.zaznameno,
                            ignition: v.ignition ?? fresh.ignition,
                            motion: v.motion ?? fresh.motion,
                            power_v: v.power_v ?? fresh.power_v,
                        };
                    }));
                })
                .catch(() => {});
        }, 30_000);

        if (!window.Echo) {
            console.warn('[FLOTILA-DIAG] window.Echo není dostupný — Reverb subscription se nenastaví');
            return;
        }

        // ─── Dočasná diagnostika cadence Reverb broadcastů ───
        // Otevři DevTools → Console, sleduj řádky "[FLOTILA-DIAG] event …".
        // Vidíš každý dorazivší event s timestampem GPS fixu i wall-clock dorazem,
        // navíc rolling stats (event/min, avg gap, max gap).
        const arrivals = []; // wall-clock arrival ms
        const fixTimes = []; // GPS fix ms (z e.zaznamoAt)
        let lastPerVeh = new Map(); // vozidloId → arrival ms

        try {
            const conn = window.Echo.connector?.pusher?.connection;
            if (conn) {
                conn.bind('state_change', (s) => {
                    console.log('[FLOTILA-DIAG] WS state:', s.previous, '→', s.current);
                });
            }
        } catch (err) {
            console.warn('[FLOTILA-DIAG] Echo connection state listener failed:', err);
        }

        const channel = window.Echo.private('flotila.live');
        const handler = (e) => {
            const now = Date.now();
            const fixMs = e.zaznamoAt ? Date.parse(e.zaznamoAt) : NaN;
            const lagSec = Number.isFinite(fixMs) ? ((now - fixMs) / 1000).toFixed(1) : '?';
            const prevSameVeh = lastPerVeh.get(e.vozidloId);
            const gapSec = prevSameVeh ? ((now - prevSameVeh) / 1000).toFixed(1) : '—';
            lastPerVeh.set(e.vozidloId, now);
            arrivals.push(now);
            if (Number.isFinite(fixMs)) fixTimes.push(fixMs);
            // Drop záznamy starší než 60 s
            const cutoff = now - 60000;
            while (arrivals.length && arrivals[0] < cutoff) arrivals.shift();
            // Stats za posledních 60 s
            const cnt = arrivals.length;
            let avgGap = '—', maxGap = '—';
            if (cnt > 1) {
                const gaps = [];
                for (let i = 1; i < arrivals.length; i++) gaps.push(arrivals[i] - arrivals[i - 1]);
                avgGap = (gaps.reduce((a, b) => a + b, 0) / gaps.length / 1000).toFixed(1) + 's';
                maxGap = (Math.max(...gaps) / 1000).toFixed(1) + 's';
            }
            console.log(
                `[FLOTILA-DIAG] event v${e.vozidloId} fix=${e.zaznamoAt} lag=${lagSec}s gap_pro_voz=${gapSec}s · 60s: count=${cnt} avg_gap=${avgGap} max_gap=${maxGap}`
            );

            setVozidla((prev) => prev.map((v) => v.id === e.vozidloId
                ? {
                    ...v,
                    lat: e.lat,
                    lng: e.lng,
                    rychlost_kmh: e.rychlostKmh,
                    smer: e.smerStupne,
                    zaznameno: e.zaznamoAt,
                    aktualni_ridic_id: e.aktualniRidicId ?? v.aktualni_ridic_id,
                    ignition: e.ignition ?? v.ignition,
                    motion: e.motion ?? v.motion,
                    power_v: e.powerV ?? v.power_v,
                }
                : v));
        };
        channel.listen('.pozice.updated', handler);
        console.log('[FLOTILA-DIAG] Reverb subscription na flotila.live nastavena');

        return () => {
            clearInterval(refetchInterval);
            try { window.Echo.leave('flotila.live'); } catch {}
        };
    }, [refetch]);

    return { vozidla, setVozidla, loading, error, refetch };
}
